import { Component } from "react"
import EventAdaptingStateHandler from "src/states/EventAdaptingStateHandler"
import StateConstants from "src/states/StateContstants"
import ApiHandler from "src/api/ApiHandler"
import StripeCheckout from "./StripeCheckout"
import CostComparison from "./CostComparison"
import FeatureList from "./FeatureList"
import "./PremiumPage.css"
import posthog from "posthog-js"


interface PlanContentProps {
    stateHandler: EventAdaptingStateHandler
    apiHandler: ApiHandler
    showSuccess?: boolean
    showCancelled?: boolean
    onPaymentComplete?: () => void
}

interface PlanContentState {
    isPremium: boolean
    isLoggedIn: boolean
    featuresLoaded: boolean
    paid: boolean
    valuationsLast24h: number | null
    valuationsAllTime: number | null
    displayedLast24h: number
}


class PlanContent extends Component<PlanContentProps, PlanContentState> {
    constructor(props: PlanContentProps) {
        super(props)

        const features = props.stateHandler.get(StateConstants.USER_FEATURES)

        this.state = {
            isPremium: props.stateHandler.get(StateConstants.IS_PREMIUM) || false,
            isLoggedIn: props.stateHandler.get(StateConstants.IS_LOGGED_IN) || false,
            featuresLoaded: features !== undefined && features !== null,
            paid: false,
            valuationsLast24h: null,
            valuationsAllTime: null,
            displayedLast24h: 0,
        }

        props.stateHandler.addListener(StateConstants.IS_PREMIUM, (isPremium: boolean) => {
            this.setState({ isPremium })
        })
        props.stateHandler.addListener(StateConstants.USER_FEATURES, () => {
            this.setState({ featuresLoaded: true })
        })
        props.stateHandler.addListener(StateConstants.IS_LOGGED_IN, (isLoggedIn: boolean) => {
            this.setState({ isLoggedIn })
        })
    }

    async componentDidMount() {
        try {
            const usage = await this.props.apiHandler.getGlobalUsage()
            if (usage) {
                this.setState({ valuationsLast24h: usage.last_24h, valuationsAllTime: usage.all_time })
                this.animateCountUp(usage.last_24h)
            }
        } catch (e) {
            // best effort
        }
    }

    animateCountUp(target: number) {
        if (target === 0) {
            this.setState({ displayedLast24h: 0 })
            return
        }
        const duration = 250
        const steps = Math.min(target, 40)
        const interval = duration / steps
        let current = 0
        const timer = setInterval(() => {
            current += Math.ceil(target / steps)
            if (current >= target) {
                current = target
                clearInterval(timer)
            }
            this.setState({ displayedLast24h: current })
        }, interval)
    }

    handleCheckoutComplete = (paymentIntentId: string) => {
        posthog?.capture("payment_completed", { payment_intent_id: paymentIntentId })
        this.setState({ paid: true })

        if (this.state.isLoggedIn) {
            this.claimAndFinish(paymentIntentId)
            return
        }

        this.props.stateHandler.set(StateConstants.PENDING_PAYMENT_INTENT_ID, paymentIntentId)
        localStorage.setItem("pendingPaymentIntentId", paymentIntentId)
        this.props.stateHandler.set(StateConstants.SHOW_PLAN_MODAL, false)
        this.props.stateHandler.set(StateConstants.SHOW_LOGIN_MODAL, true)
    }

    async claimAndFinish(paymentIntentId: string) {
        try {
            await this.props.apiHandler.claimPayment(paymentIntentId)
        } catch {}
        if (this.props.onPaymentComplete) {
            this.props.onPaymentComplete()
        }
    }

    render() {
        return (
            <>
                {this.props.showSuccess && (
                    <div className="premium-success-banner">
                        <span className="success-title">Welcome to Lifetime Access!</span>
                        <span className="success-subtitle">You now have full access to every feature, forever. Welcome.</span>
                    </div>
                )}
                {this.props.showCancelled && (
                    <div className="premium-cancelled-banner">
                        Checkout was cancelled. You can upgrade anytime.
                    </div>
                )}
                <div className="premium-header">
                    <h1 className="premium-page-title">Lifetime Access</h1>
                    <p className="premium-page-subtitle">Know what any home in Ireland is worth — before you bid, list, or sell.</p>
                </div>
                <CostComparison />
                <FeatureList />
                {(this.state.isPremium || this.state.paid) && (
                    <div className="stripe-checkout-lifetime-active">
                        <p className="stripe-checkout-lifetime-title">Lifetime Access Active</p>
                        <p className="stripe-checkout-lifetime-subtitle">You have full access to every feature, forever.</p>
                    </div>
                )}
                {!this.state.isPremium && !this.state.paid && (
                    <StripeCheckout
                        stateHandler={this.props.stateHandler}
                        apiHandler={this.props.apiHandler}
                        isLoggedIn={this.state.isLoggedIn}
                        onComplete={this.handleCheckoutComplete}
                        onCancel={() => this.props.stateHandler.set(StateConstants.SHOW_PLAN_MODAL, false)}
                        onLoginRedirect={() => {
                            this.props.stateHandler.set(StateConstants.SHOW_PLAN_MODAL, false)
                            this.props.stateHandler.set(StateConstants.SHOW_LOGIN_MODAL, true)
                        }}
                    />
                )}
                {this.state.valuationsLast24h !== null && (
                    <p className="premium-social-proof">
                        <strong className="social-proof-count">{this.state.displayedLast24h.toLocaleString()}</strong> users have valued homes in the last 24 hours
                    </p>
                )}
            </>
        )
    }
}

export default PlanContent
